import type { FloraCatalog } from '@/flora/FloraCatalog';
import type { FloraDefinition } from '@/flora/FloraDefinition';
import type { FloraInstance } from '@/flora/FloraInstance';
import { MAX_OVERGROWN_MATURITY } from '@/flora/FloraInstance';
import type { FloraEditor } from '@/editor/FloraEditor';
import type { SimpleSeason } from '@/environment/EnvironmentState';
import { clear, el, formatLength, setClass, type Child } from './dom';
import { icon } from './Icons';
import { floraThumbnailSvg, seasonalSwatches } from './FloraThumbnail';
import { categoryLabel, fullDisplayName } from './FloraFacets';
import { computePlantMetrics, formatAge, formatDayOfYear } from './PlantMetrics';
import type { UiPreferences } from './UiPreferences';
import { tagCategoryInfo } from '@/semantics/SemanticTag';
import type { SemanticProfile } from '@/semantics/SemanticProfile';

/**
 * Right-hand contextual inspector (spec §14, §19, milestone 6.5).
 *
 * Three states, chosen by what is selected: site summary (nothing selected), full botanical record
 * plus editable modifiers (one plant), and shared bulk controls (several plants). All edits route
 * through {@link FloraEditor.applyInstanceEdits} so they are undoable and propagate to linked
 * aliases; continuous sliders share a merge key so a drag is one history entry.
 */

export interface SiteSummary {
  widthMeters: number;
  lengthMeters: number;
  templateName: string;
}

export interface InspectorDeps {
  editor: FloraEditor;
  catalog: FloraCatalog;
  preferences: UiPreferences;
  season: () => SimpleSeason;
  dayOfYear: () => number;
  site: () => SiteSummary | null;
  /**
   * Everything the AI render will be told about a plant — catalog tags, this specimen's condition,
   * and anything tagged on it by hand. Optional so a headless/test inspector can be built without the
   * semantics layer.
   */
  semanticsFor?: (instanceId: string) => SemanticProfile | null;
  /** Whole-site semantics, for the no-selection view. */
  siteSemantics?: () => SemanticProfile | null;
  /** Arm the tag tool. The inspector shows semantics; it does not edit them — see below. */
  onTagTool?: () => void;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export class InspectorPanel {
  readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly titleEl: HTMLElement;

  /**
   * True while a slider is being dragged. Editing fires an editor command on every `input` event,
   * which loops back here as a change notification — rebuilding the panel mid-drag would tear the
   * slider out of the DOM and the drag would die after one frame. Rebuilds are deferred to pointerup.
   */
  private dragging = false;
  private pendingRefresh = false;
  private readonly onPointerDown = (event: PointerEvent): void => {
    if ((event.target as HTMLElement | null)?.closest('.lg-slider')) this.dragging = true;
  };
  private readonly onPointerUp = (): void => {
    if (!this.dragging) return;
    this.dragging = false;
    if (this.pendingRefresh) this.refresh();
  };

  constructor(private readonly deps: InspectorDeps) {
    this.body = el('div', { class: 'lg-panel__body' });
    this.titleEl = el('h2', { class: 'lg-panel__title', text: 'Site' });

    this.root = el(
      'aside',
      { class: 'lg-panel lg-panel--right', attrs: { 'aria-label': 'Inspector' } },
      el(
        'div',
        { class: 'lg-panel__head' },
        el('span', { class: 'lg-panel__icon', html: icon('info', 16) }),
        this.titleEl,
        el('button', {
          class: 'lg-panel__collapse',
          html: icon('chevron', 16),
          title: 'Collapse inspector',
          attrs: { type: 'button', 'aria-label': 'Collapse inspector' },
          on: { click: () => this.setCollapsed(!this.isCollapsed) },
        }),
      ),
      this.body,
    );

    this.root.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);

    this.setCollapsed(deps.preferences.collapsed('right'));
    this.refresh();
  }

  get isCollapsed(): boolean {
    return this.root.classList.contains('is-collapsed');
  }

  setCollapsed(collapsed: boolean): void {
    setClass(this.root, 'is-collapsed', collapsed);
    this.deps.preferences.setCollapsed('right', collapsed);
    this.deps.onCollapsedChange?.(collapsed);
  }

  private get unit(): 'meters' | 'feet' {
    return this.deps.preferences.unit;
  }

  refresh(): void {
    if (this.dragging) {
      this.pendingRefresh = true;
      return;
    }
    this.pendingRefresh = false;

    const selected = this.deps.editor.selection
      .selected()
      .map((id) => this.deps.editor.world.get(id))
      .filter((instance): instance is FloraInstance => instance !== undefined);

    const staffage = this.deps.editor.selectedStaffage();

    clear(this.body);
    if (selected.length === 0 && staffage.length > 0) {
      // Creatures and objects have no inspector of their own yet. What they must not get is the
      // "nothing is selected" panel while three of them are highlighted on the canvas.
      this.titleEl.textContent = staffage.length === 1 ? 'Selection' : `${staffage.length} selected`;
      this.renderStaffage(staffage);
    } else if (selected.length === 0) {
      this.titleEl.textContent = 'Site';
      this.renderSite();
    } else if (selected.length === 1 && selected[0]) {
      const instance = selected[0];
      const definition = this.deps.catalog.get(instance.floraDefinitionId);
      this.titleEl.textContent = 'Plant';
      this.renderSingle(definition, instance);
    } else {
      this.titleEl.textContent = `${selected.length} plants`;
      this.renderMultiple(selected);
    }
  }

  /* -------------------------------- Nothing selected ------------------------------- */

  private renderSite(): void {
    const site = this.deps.site();
    const instances = this.deps.editor.world.all();
    const species = new Set(instances.map((i) => i.floraDefinitionId));
    const plantedArea = instances.reduce((total, instance) => {
      const definition = this.deps.catalog.get(instance.floraDefinitionId);
      const radius = computePlantMetrics(definition, instance).growth.spreadMeters / 2;
      return total + Math.PI * radius * radius;
    }, 0);

    this.body.appendChild(
      this.section('Canvas', [
        site && this.field('Dimensions', `${formatLength(site.widthMeters, this.unit)} × ${formatLength(site.lengthMeters, this.unit)}`),
        site && this.field('Template', site.templateName),
        this.field('Plants', String(instances.length)),
        this.field('Species', String(species.size)),
        this.field('Planted area', `${plantedArea.toFixed(1)} m²`),
      ]),
    );

    this.body.appendChild(
      this.section('Date', [
        this.field('Season', this.deps.season()),
        this.field('Day', formatDayOfYear(this.deps.dayOfYear())),
        this.field('Catalog', this.deps.catalog.version),
      ]),
    );

    const siteSemantics = this.deps.siteSemantics?.() ?? null;
    this.body.appendChild(
      this.section('Meaning', [
        this.semanticsBlock(
          siteSemantics,
          'Nothing tagged yet. Tag the site to tell every AI render what this garden is.',
        ),
      ]),
    );

    this.body.appendChild(
      el(
        'div',
        { class: 'lg-hint' },
        el('p', { text: 'Select a plant to inspect it, or pick one from the palette and press P to place.' }),
      ),
    );
  }

  /* -------------------------------- One plant selected ----------------------------- */

  private renderSingle(definition: FloraDefinition, instance: FloraInstance): void {
    const metrics = computePlantMetrics(definition, instance);

    this.body.appendChild(
      el(
        'div',
        { class: 'lg-identity' },
        el('span', {
          class: 'lg-identity__thumb',
          html: floraThumbnailSvg(definition, { season: this.deps.season() }),
        }),
        el(
          'div',
          { class: 'lg-identity__text' },
          el('span', { class: 'lg-identity__name', text: fullDisplayName(definition) }),
          el('span', { class: 'lg-identity__latin', text: definition.botanicalName }),
          el('span', { class: 'lg-identity__category', text: categoryLabel(definition.category) }),
        ),
      ),
    );

    this.body.appendChild(
      this.section('Size', [
        this.field('Current', `${formatLength(metrics.growth.heightMeters, this.unit)} H × ${formatLength(metrics.growth.spreadMeters, this.unit)} W`),
        this.field('At maturity', `${formatLength(metrics.matureHeightMeters, this.unit)} H × ${formatLength(metrics.matureSpreadMeters, this.unit)} W`),
        this.field('Approx. age', formatAge(metrics.approximateAgeYears)),
        metrics.nearestContainerStage && this.field('Nursery stage', metrics.nearestContainerStage.id),
        this.field('Canopy area', `${metrics.canopyAreaSquareMeters.toFixed(1)} m²`),
      ]),
    );

    this.body.appendChild(
      this.section('Growth', [
        this.slider({
          label: 'Maturity',
          value: instance.maturity,
          min: 0,
          max: MAX_OVERGROWN_MATURITY,
          step: 0.01,
          format: () => `${metrics.maturityPercent}%`,
          onInput: (value) => this.edit([instance], { maturity: value }, 'Set maturity', 'maturity'),
        }),
        this.slider({
          label: 'Fullness',
          value: instance.fullness,
          min: 0.2,
          max: 1.4,
          step: 0.01,
          format: (v) => `${Math.round(v * 100)}%`,
          onInput: (value) => this.edit([instance], { fullness: value }, 'Set fullness', 'fullness'),
        }),
        this.slider({
          label: 'Health',
          value: instance.health,
          min: 0.2,
          max: 1,
          step: 0.01,
          format: (v) => `${Math.round(v * 100)}%`,
          onInput: (value) => this.edit([instance], { health: value }, 'Set health', 'health'),
        }),
        this.slider({
          label: 'Size variation',
          value: instance.sizeVariation,
          min: -0.4,
          max: 0.4,
          step: 0.01,
          format: (v) => `${v > 0 ? '+' : ''}${Math.round(v * 100)}%`,
          onInput: (value) => this.edit([instance], { sizeVariation: value }, 'Set size variation', 'size'),
        }),
      ]),
    );

    this.body.appendChild(
      this.section('Placement', [
        this.field('Position', `x ${instance.position.x.toFixed(2)}  z ${instance.position.z.toFixed(2)}`),
        this.slider({
          label: 'Rotation',
          value: instance.rotation.y,
          min: 0,
          max: Math.PI * 2,
          step: 0.01,
          format: (v) => `${Math.round((v * 180) / Math.PI)}°`,
          onInput: (value) =>
            this.edit(
              [instance],
              { rotation: { ...instance.rotation, y: value } },
              'Rotate plant',
              'rotate',
            ),
        }),
        this.field('Recommended spacing', formatLength(definition.placement.recommendedSpacingMeters, this.unit)),
        this.field('Max slope', `${definition.placement.maxSlopeDegrees}°`),
      ]),
    );

    this.body.appendChild(
      this.section('Conditions', [
        this.field('Sun', definition.sunlight.map((s) => s.replace('-', ' ')).join(', ')),
        this.field('Moisture', definition.moisture.join(', ')),
        definition.hardinessZones &&
          this.field('Hardiness', `Zone ${definition.hardinessZones.minimum}–${definition.hardinessZones.maximum}`),
      ]),
    );

    // Everything the render will be told about this plant, in one place. Catalog tags used to be
    // shown here as decoration — they were the only semantics the app had, and nothing downstream
    // read them. Now they are one source among several feeding the same prompt, so they are shown
    // where the manual tags are, and labelled as what they are.
    this.body.appendChild(
      this.section('Meaning — sent with every AI render', [
        this.semanticsBlock(
          this.deps.semanticsFor?.(instance.id) ?? null,
          'Nothing tagged. Press Shift+T and click this plant to say what it is.',
        ),
      ]),
    );

    this.body.appendChild(this.section('Seasonal interest', [this.seasonStrip(definition)]));

    const overridden = instance.seasonOverrideDayOfYear !== undefined;
    this.body.appendChild(
      this.section('Season', [
        this.slider({
          label: 'Seasonal offset',
          value: instance.seasonalOffsetDays,
          min: -30,
          max: 30,
          step: 1,
          format: (v) => `${v > 0 ? '+' : ''}${Math.round(v)} d`,
          onInput: (value) =>
            this.edit([instance], { seasonalOffsetDays: Math.round(value) }, 'Set seasonal offset', 'offset'),
        }),
        this.toggle('Freeze at a date', overridden, (on) =>
          on
            ? this.edit([instance], { seasonOverrideDayOfYear: this.deps.dayOfYear() }, 'Freeze season')
            : this.editClear([instance], ['seasonOverrideDayOfYear'], 'Follow global season'),
        ),
        overridden &&
          this.slider({
            label: 'Frozen at',
            value: instance.seasonOverrideDayOfYear ?? 172,
            min: 1,
            max: 365,
            step: 1,
            format: (v) => formatDayOfYear(v),
            onInput: (value) =>
              this.edit(
                [instance],
                { seasonOverrideDayOfYear: Math.round(value) },
                'Set frozen date',
                'override',
              ),
          }),
      ]),
    );

    this.body.appendChild(
      this.section('State', [
        this.toggle('Locked', instance.locked, (on) => this.edit([instance], { locked: on }, on ? 'Lock plant' : 'Unlock plant')),
        this.toggle('Hidden', instance.hidden, (on) => this.edit([instance], { hidden: on }, on ? 'Hide plant' : 'Show plant')),
        instance.aliasGroupId !== undefined && this.field('Alias group', instance.aliasGroupId),
        instance.groupId !== undefined && this.field('Group', instance.groupId),
        this.field('Model', this.modelSourceLabel(instance)),
      ]),
    );
  }

  /** Which representation is currently drawing this plant — procedural, or a registered asset. */
  private modelSourceLabel(instance: FloraInstance): string {
    const view = this.deps.editor.world.view(instance.id);
    const providerId = view?.userData.floraModelProviderId as unknown;
    return typeof providerId === 'string' ? providerId : 'Procedural (placeholder)';
  }

  /* ------------------------------ Several plants selected -------------------------- */

  private renderMultiple(instances: FloraInstance[]): void {
    const speciesCounts = new Map<string, number>();
    for (const instance of instances) {
      speciesCounts.set(instance.floraDefinitionId, (speciesCounts.get(instance.floraDefinitionId) ?? 0) + 1);
    }
    const averageMaturity =
      instances.reduce((total, instance) => total + instance.maturity, 0) / instances.length;

    this.body.appendChild(
      this.section(
        'Selection',
        [...speciesCounts.entries()].map(([id, count]) => {
          const definition = this.deps.catalog.find(id);
          return this.field(definition ? fullDisplayName(definition) : id, `${count}`);
        }),
      ),
    );

    this.body.appendChild(
      this.section('Apply to all', [
        this.slider({
          label: 'Maturity',
          value: averageMaturity,
          min: 0,
          max: MAX_OVERGROWN_MATURITY,
          step: 0.01,
          format: (v) => `${Math.round(v * 100)}%`,
          onInput: (value) => this.edit(instances, { maturity: value }, 'Set maturity', 'maturity-multi'),
        }),
        this.slider({
          label: 'Fullness',
          value: instances.reduce((t, i) => t + i.fullness, 0) / instances.length,
          min: 0.2,
          max: 1.4,
          step: 0.01,
          format: (v) => `${Math.round(v * 100)}%`,
          onInput: (value) => this.edit(instances, { fullness: value }, 'Set fullness', 'fullness-multi'),
        }),
        this.toggle('Locked', instances.every((i) => i.locked), (on) =>
          this.edit(instances, { locked: on }, on ? 'Lock plants' : 'Unlock plants'),
        ),
        this.toggle('Hidden', instances.every((i) => i.hidden), (on) =>
          this.edit(instances, { hidden: on }, on ? 'Hide plants' : 'Show plants'),
        ),
      ]),
    );

    this.body.appendChild(
      el(
        'div',
        { class: 'lg-actions' },
        this.action('Duplicate', 'duplicate', () => this.deps.editor.duplicateSelected()),
        this.action('Link as alias', 'link', () => this.deps.editor.linkSelected()),
        this.action('Group', 'group', () => this.deps.editor.groupSelected()),
        this.action('Delete', 'delete', () => this.deps.editor.deleteSelected(), true),
      ),
    );
  }

  /* ------------------------- Creatures and objects selected ------------------------ */

  /**
   * What a selection of staffage gets, until each class has an inspector of its own: its name, and
   * the two things it can already do. Deliberately short — inventing controls here that the records
   * do not yet support would be worse than an honest gap.
   */
  private renderStaffage(items: readonly { kind: 'fauna' | 'object'; name: string }[]): void {
    const counts = new Map<string, number>();
    for (const item of items) counts.set(item.name, (counts.get(item.name) ?? 0) + 1);

    this.body.appendChild(
      this.section(
        items.some((item) => item.kind === 'object') && items.some((item) => item.kind === 'fauna')
          ? 'Creatures and objects'
          : items[0]?.kind === 'object'
            ? 'Objects'
            : 'Creatures',
        [...counts.entries()].map(([name, count]) =>
          this.field(name.charAt(0).toUpperCase() + name.slice(1), `${count}`),
        ),
      ),
    );

    this.body.appendChild(
      el(
        'div',
        { class: 'lg-hint' },
        el('p', { text: 'G to move · R to turn · Delete to remove.' }),
      ),
    );

    this.body.appendChild(
      el(
        'div',
        { class: 'lg-actions' },
        this.action('Delete', 'delete', () => this.deps.editor.deleteSelected(), true),
      ),
    );
  }

  /* --------------------------------- Building blocks ------------------------------- */

  private edit(
    instances: readonly FloraInstance[],
    patch: Parameters<FloraEditor['applyInstanceEdits']>[0][number]['patch'],
    label: string,
    mergeKey?: string,
  ): void {
    this.deps.editor.applyInstanceEdits(
      instances.map((instance) => ({ id: instance.id, patch })),
      label,
      mergeKey,
    );
  }

  private editClear(
    instances: readonly FloraInstance[],
    clearFields: Parameters<FloraEditor['applyInstanceEdits']>[0][number]['clear'],
    label: string,
  ): void {
    this.deps.editor.applyInstanceEdits(
      instances.map((instance) => ({ id: instance.id, patch: {}, ...(clearFields ? { clear: clearFields } : {}) })),
      label,
    );
  }

  /**
   * A profile as read-only chips, plus a way through to the tool that edits them.
   *
   * Read-only on purpose. The tag bubble already offers the full editor against a clicked target, and
   * a second editor here would be a second place for the same tag to be typed differently — and would
   * have to solve scope (this plant? this species? this spot?) all over again, in a panel with no
   * click to infer it from. Showing the result and handing off is the honest split.
   */
  private semanticsBlock(profile: SemanticProfile | null, emptyHint: string): HTMLElement {
    const descriptors = profile?.descriptors ?? [];
    const block = el('div', { class: 'lg-semantics' });

    if (descriptors.length === 0) {
      block.appendChild(el('span', { class: 'lg-semantics__empty', text: emptyHint }));
    } else {
      for (const descriptor of descriptors) {
        const info = tagCategoryInfo(descriptor.category);
        const title =
          descriptor.source === 'manual'
            ? `${info.name} · tagged by hand${descriptor.note !== undefined ? ` · ${descriptor.note}` : ''}`
            : `${info.name} · from the catalog — no need to retype it`;
        block.appendChild(
          el(
            'span',
            {
              class: `lg-tagchip lg-tagchip--${descriptor.emphasis}${descriptor.source === 'manual' ? '' : ' is-implicit'}`,
              attrs: { style: `--tag-color:${info.color}` },
              title,
            },
            el('span', { class: 'lg-tagchip__dot' }),
            el('span', { class: 'lg-tagchip__text', text: descriptor.label }),
          ),
        );
      }
    }

    if (this.deps.onTagTool) {
      block.appendChild(
        el('button', {
          class: 'lg-chip lg-chip--ghost',
          text: descriptors.length === 0 ? 'Tag…' : 'Edit tags…',
          title: 'Arm the tag tool, then click what you want to describe  ·  Shift+T',
          attrs: { type: 'button' },
          on: { click: () => this.deps.onTagTool?.() },
        }),
      );
    }

    return block;
  }

  private section(title: string, children: Child[]): HTMLElement {
    return el(
      'section',
      { class: 'lg-section' },
      el('h3', { class: 'lg-section__title', text: title }),
      el('div', { class: 'lg-section__body' }, ...children),
    );
  }

  private field(label: string, value: string): HTMLElement {
    return el(
      'div',
      { class: 'lg-field' },
      el('span', { class: 'lg-field__label', text: label }),
      el('span', { class: 'lg-field__value', text: value }),
    );
  }

  private slider(options: {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    format: (value: number) => string;
    onInput: (value: number) => void;
  }): HTMLElement {
    const readout = el('span', { class: 'lg-field__value', text: options.format(options.value) });
    const input = el('input', {
      class: 'lg-slider',
      attrs: {
        type: 'range',
        min: String(options.min),
        max: String(options.max),
        step: String(options.step),
        value: String(options.value),
        'aria-label': options.label,
      },
      on: {
        input: (event) => {
          const value = Number((event.target as HTMLInputElement).value);
          readout.textContent = options.format(value);
          options.onInput(value);
        },
      },
    });
    return el(
      'div',
      { class: 'lg-control' },
      el(
        'div',
        { class: 'lg-control__head' },
        el('span', { class: 'lg-field__label', text: options.label }),
        readout,
      ),
      input,
    );
  }

  private toggle(label: string, value: boolean, onChange: (value: boolean) => void): HTMLElement {
    return el(
      'label',
      { class: 'lg-switch' },
      el('span', { class: 'lg-field__label', text: label }),
      el('input', {
        attrs: { type: 'checkbox', checked: value },
        on: { change: (event) => onChange((event.target as HTMLInputElement).checked) },
      }),
      el('span', { class: 'lg-switch__track' }, el('span', { class: 'lg-switch__thumb' })),
    );
  }

  private seasonStrip(definition: FloraDefinition): HTMLElement {
    const strip = el('div', { class: 'lg-seasons' });
    for (const { season, color } of seasonalSwatches(definition)) {
      strip.appendChild(
        el(
          'div',
          { class: `lg-seasons__item${season === this.deps.season() ? ' is-active' : ''}`, title: season },
          el('span', { class: 'lg-seasons__dot', style: { background: color } }),
          el('span', { class: 'lg-seasons__label', text: season.slice(0, 3) }),
        ),
      );
    }
    return strip;
  }

  private action(
    label: string,
    iconName: Parameters<typeof icon>[0],
    onClick: () => void,
    danger = false,
  ): HTMLElement {
    return el('button', {
      class: `lg-button${danger ? ' lg-button--danger' : ''}`,
      html: `${icon(iconName, 15)}<span>${label}</span>`,
      attrs: { type: 'button' },
      on: { click: onClick },
    });
  }

  dispose(): void {
    this.root.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.root.remove();
  }
}
